/**
 * @file not-found-error.ts
 * @description Détection duck-typed d'une erreur HTTP « ressource introuvable » (404),
 * sans dépendre du type concret ni du provider (Graph / Google).
 *
 * Motivation (AB#1764) : quand un mail a déjà été supprimé côté messagerie (Outlook / hors
 * MailVista), l'action de suppression du SDK (`moveMail` / `deleteMail`) reçoit un 404. La
 * suppression doit être **idempotente** : un 404 (message introuvable) = le mail est déjà dans
 * l'état voulu → à traiter comme un succès / no-op, pas comme une erreur.
 *
 * Fiabilité en deux temps :
 *  - propriété `status === 404` — attachée au throw HTTP des providers
 *    (`callGraphEndPoint`, `callGoogleApi`) et préservée sur les ré-emballages
 *    (`moveMail`/`deleteMail`, `DataManager`) ;
 *  - fallback regex sur le message (`HTTP error! status: 404`) — filet pour les couches qui
 *    stringifient encore l'erreur et perdraient la propriété.
 *
 * Analogue à `isThrottleError` (429) — cf. `utils/throttle/throttle-error.ts`.
 */
export function isNotFoundError(error: unknown): boolean {
    if (!error) return false;

    const status = (error as { status?: unknown }).status;
    if (status === 404) return true;

    const message =
        typeof error === "string"
            ? error
            : typeof (error as { message?: unknown }).message === "string"
              ? (error as { message: string }).message
              : "";

    return /status:\s*404\b/.test(message);
}
